import { api } from "./api";
import { Project } from "@/types/models";

interface ProjectApiResponse {
  id: string;
  name: string;
  code: string;
  kantata_project_id: string | null;
  client_name: string | null;
  client_id: string | null;
  status: string;
  start_date: string;
  team_members: number;
  therapy_area: string | null;
  indication: string | null;
  condition: string | null;
  objective: string | null;
  project_type: string | null;
  model: string;
  completion: number;
  days_in_progress: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  member_ids: string[];
  visible_chat_filter_keys: string[] | null;
  is_advanced_rag: boolean;
}

function toProject(r: ProjectApiResponse): Project {
  return {
    id: r.id,
    name: r.name,
    code: r.code,
    clientName: r.client_name ?? "",
    kantataProjectId: r.kantata_project_id ?? null,
    clientId: r.client_id ?? null,
    status: r.status,
    startDate: r.start_date,
    teamMembers: r.team_members,
    therapyArea: r.therapy_area || "",
    indication: r.indication || "",
    condition: r.condition || "",
    objective: r.objective || "",
    projectType: r.project_type || "",
    model: r.model,
    completion: r.completion,
    daysInProgress: r.days_in_progress,
    memberIds: r.member_ids ?? [],
    documents: [],
    picoExtractions: [],
    createdAt: r.created_at,
    createdBy: r.created_by ?? null,
    visibleChatFilterKeys: r.visible_chat_filter_keys,
    isAdvancedRag: r.is_advanced_rag ?? false,
  };
}

export interface ProjectCreateInput {
  name: string;
  code: string;
  kantataProjectId?: string;
  clientId?: string;
  clientName?: string;
  startDate: string;
  therapyArea?: string;
  indication?: string;
  condition?: string;
  objective?: string;
  projectType?: string;
  model?: string;
  memberIds?: string[];
  isAdvancedRag?: boolean;
  /** AI permission option keys confirmed at creation (they can't be added later). */
  aiPermissions?: string[];
}

export interface ProjectDocumentsResponse {
  inserted: number;
  updated: number;
  deleted: number;
  total: number;
}

interface ProjectDocumentItemApiResponse {
  project_id: string;
  document_id: string;
  is_active: boolean;
  custom_field_values: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectDocumentItem {
  projectId: string;
  documentId: string;
  isActive: boolean;
  customFieldValues: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export const projectsApi = {
  list: async (): Promise<Project[]> => {
    const data = await api.get<ProjectApiResponse[]>("/api/projects");
    return data.map(toProject);
  },

  get: async (id: string): Promise<Project> => {
    const data = await api.get<ProjectApiResponse>(`/api/projects/${id}`);
    return toProject(data);
  },

  create: async (project: ProjectCreateInput): Promise<Project> => {
    const body = {
      name: project.name,
      code: project.code,
      kantata_project_id: project.kantataProjectId || undefined,
      client_id: project.clientId || undefined,
      client_name: project.clientName || undefined,
      start_date: project.startDate,
      therapy_area: project.therapyArea,
      indication: project.indication,
      condition: project.condition,
      objective: project.objective,
      project_type: project.projectType,
      model: project.model ?? "gpt-52",
      member_ids: project.memberIds ?? [],
      is_advanced_rag: project.isAdvancedRag ?? false,
      ...(project.aiPermissions ? { ai_permissions: project.aiPermissions } : {}),
    };
    const data = await api.post<ProjectApiResponse>("/api/projects", body);
    return toProject(data);
  },

  update: async (
    id: string,
    project: Partial<ProjectCreateInput> & {
      status?: string;
      teamMembers?: number;
      completion?: number;
      daysInProgress?: number;
      visibleChatFilterKeys?: string[] | null;
      isAdvancedRag?: boolean;
    }
  ): Promise<Project> => {
    const body: Record<string, unknown> = {};
    if (project.name !== undefined) body.name = project.name;
    if (project.code !== undefined) body.code = project.code;
    if (project.kantataProjectId !== undefined)
      body.kantata_project_id = project.kantataProjectId || null;
    if (project.clientId !== undefined)
      body.client_id = project.clientId || null;
    if (project.clientName !== undefined)
      body.client_name = project.clientName;
    if (project.status !== undefined) body.status = project.status;
    if (project.teamMembers !== undefined)
      body.team_members = project.teamMembers;
    if (project.therapyArea !== undefined)
      body.therapy_area = project.therapyArea;
    if (project.indication !== undefined)
      body.indication = project.indication;
    if (project.condition !== undefined)
      body.condition = project.condition;
    if (project.objective !== undefined) body.objective = project.objective;
    if (project.projectType !== undefined)
      body.project_type = project.projectType;
    if (project.model !== undefined) body.model = project.model;
    if (project.completion !== undefined)
      body.completion = project.completion;
    if (project.daysInProgress !== undefined)
      body.days_in_progress = project.daysInProgress;
    if (project.memberIds !== undefined)
      body.member_ids = project.memberIds;
    // null is meaningful here — it clears the override back to "show all".
    // Only skip the field when the caller explicitly didn't set it.
    if (project.visibleChatFilterKeys !== undefined)
      body.visible_chat_filter_keys = project.visibleChatFilterKeys;
    if (project.isAdvancedRag !== undefined)
      body.is_advanced_rag = project.isAdvancedRag;
    const data = await api.put<ProjectApiResponse>(
      `/api/projects/${id}`,
      body
    );
    return toProject(data);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/api/projects/${id}`);
  },

  insertDocuments: async (
    projectId: string,
    documentIds: string[],
    customFieldValues?: Record<string, Record<string, unknown>>
  ): Promise<ProjectDocumentsResponse> => {
    return api.post<ProjectDocumentsResponse>("/api/projects/insert-documents", {
      item: {
        project_id: projectId,
        document_ids: documentIds,
        custom_field_values: customFieldValues,
      },
    });
  },

  deleteDocuments: async (
    projectId: string,
    documentIds: string[]
  ): Promise<ProjectDocumentsResponse> => {
    return api.post<ProjectDocumentsResponse>("/api/projects/delete-documents", {
      item: { project_id: projectId, document_ids: documentIds },
    });
  },

  syncDocuments: async (
    projectId: string,
    documentIds: string[]
  ): Promise<ProjectDocumentsResponse> => {
    return api.post<ProjectDocumentsResponse>("/api/projects/sync-documents", {
      item: { project_id: projectId, document_ids: documentIds },
    });
  },

  updateDocumentCustomFieldValues: async (
    projectId: string,
    documentId: string,
    customFieldValues: Record<string, unknown>,
  ): Promise<ProjectDocumentItem> => {
    const data = await api.patch<ProjectDocumentItemApiResponse>(
      `/api/projects/${projectId}/documents/${documentId}/custom-field-values`,
      { custom_field_values: customFieldValues },
    );
    return {
      projectId: data.project_id,
      documentId: data.document_id,
      isActive: data.is_active,
      customFieldValues: data.custom_field_values ?? null,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  getProjectDocuments: async (
    projectId: string
  ): Promise<ProjectDocumentItem[]> => {
    try {
      const data = await api.get<ProjectDocumentItemApiResponse[]>(
        `/api/projects/${projectId}/documents`
      );
      return data.map((d) => ({
        projectId: d.project_id,
        documentId: d.document_id,
        isActive: d.is_active,
        customFieldValues: d.custom_field_values ?? null,
        createdAt: d.created_at,
        updatedAt: d.updated_at,
      }));
    } catch {
      return [];
    }
  },

  getDocumentIds: async (projectId: string): Promise<string[]> => {
    try {
      const data = await api.get<unknown>(`/api/projects/${projectId}/documents`);
      if (!Array.isArray(data) || data.length === 0) return [];
      const first = data[0];
      // Plain string array: ["id1", "id2"]
      if (typeof first === "string") return data;
      // Object array: [{id: "..."}, ...] or [{document_id: "..."}, ...]
      if (first && typeof first === "object") {
        return (data as Record<string, unknown>[])
          .map((d) => (d.id ?? d.document_id) as string)
          .filter(Boolean);
      }
      return [];
    } catch {
      return [];
    }
  },

};
